import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { jsPDF } from "jspdf";
import { applyPlugin } from "jspdf-autotable";
import {LuReceiptText } from "react-icons/lu";

import { notify } from "../../../common/Toast";
import api from "../../../interceptor/Interceptor";
import { FILE_UPLOAD_URL, FILE_DOWNLOAD_URL, ORG_GET, EMAIL_PO, SUPPLIER_BY_NAME } from "../../../api/ApiEndpoints";
import { incrementPoNum, updateLastPoNum, generatePO, applyQuotation } from "../../../redux/action/requestsAction/RequestsAction";
import { BLANK_PARTY, PAYMENT_TERMS, PROJECTS, DEFAULT_TERMS, EMAIL_REGEX } from "../../../utils/Constant";
import { formatPhone, isValidPhone } from "../../../utils/phone";
import Dialog from "../../../common/Dialog";
import StepIndicator from "../../../common/StepIndicator";

import ItemReviewStep from "./ItemReviewStep";
import QuotationStep from "./QuotationStep";
import PartyDetailsStep from "./PartyDetailsStep";
import TermsStep from "./TermsStep";
import ReviewStep from "./ReviewStep";
import PurchaseOrderFooter from "../../../common/PurchaseOrderFooter";
import { generatePurchaseOrderPdf } from "../../../utils/generatePurchaseOrderPdf";

applyPlugin(jsPDF);

const makeItemKey = (item) => `${item.item}, ${item.manufacturer}, ${item.catalog}`;
const money = (n) =>
  new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR" }).format(n || 0);

export default function PurchaseOrder({ supplier, items, cellPhoneView, onGenerated }) {
  const dispatch = useDispatch();
  const user = useSelector((state) => state.auth.user);
  const { lastPoNum } = useSelector((state) => state.request);

  const [open, setOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [submitting, setSubmitting] = useState(false);

  // .................... Item review ...........................
  const [itemChecked, setItemChecked] = useState([]);
  const [discountRate, setDiscountRate] = useState([]);
  const [taxRate, setTaxRate] = useState([]);
  const [subtotals, setSubtotals] = useState([]);

  const recalcSubtotal = (index, checked, discount, tax) => {
    if (!checked) return 0;
    return Number((items[index].numUnits * items[index].unitPrice * (1 - discount) * (1 + tax)).toFixed(2));
  };

  const handleItemCheck = (index, checked) => {
    setItemChecked((prev) => {
      const next = [...prev];
      next[index] = checked;
      return next;
    });
    setSubtotals((prev) => {
      const next = [...prev];
      next[index] = recalcSubtotal(index, checked, discountRate[index], taxRate[index]);
      return next;
    });
    setQuoteItemSelection((prev) => {
      const next = { ...prev };
      if (checked) next[items[index].id] = makeItemKey(items[index]);
      else delete next[items[index].id];
      return next;
    });
  };

  const handleDiscountChange = (index, value) => {
    const rate = Math.max(0, Math.min(1, Number(value) / 100 || 0));
    setDiscountRate((prev) => {
      const next = [...prev];
      next[index] = rate;
      return next;
    });
    setSubtotals((prev) => {
      const next = [...prev];
      next[index] = recalcSubtotal(index, itemChecked[index], rate, taxRate[index]);
      return next;
    });
  };

  const handleTaxChange = (index, value) => {
    const rate = Math.max(0, Number(value) / 100 || 0);
    setTaxRate((prev) => {
      const next = [...prev];
      next[index] = rate;
      return next;
    });
    setSubtotals((prev) => {
      const next = [...prev];
      next[index] = recalcSubtotal(index, itemChecked[index], discountRate[index], rate);
      return next;
    });
  };

  // .................... Quotations ...........................
  const [quoteItemSelection, setQuoteItemSelection] = useState({}); // { [itemId]: label }
  const [selectedQuoteItemIds, setSelectedQuoteItemIds] = useState([]);
  const [pendingFile, setPendingFile] = useState(null);
  const [quoteData, setQuoteData] = useState([]); // [{ file, items: { [itemId]: label } }]
  const [selectedQuotationNames, setSelectedQuotationNames] = useState([]);

  const toggleQuoteItemSelect = (id) => {
    setSelectedQuoteItemIds((prev) =>
      prev.includes(id) ? prev.filter((v) => v !== id) : [...prev, id],
    );
  };

  const handleQuoteApply = () => {
    if (selectedQuoteItemIds.length === 0 || !pendingFile) return;
    const selectedLabels = Object.fromEntries(
      selectedQuoteItemIds.map((id) => [id, quoteItemSelection[id]]),
    );
    setQuoteData((prev) => [...prev, { file: pendingFile, items: selectedLabels }]);
    setQuoteItemSelection((prev) => {
      const next = { ...prev };
      selectedQuoteItemIds.forEach((id) => delete next[id]);
      return next;
    });
    setSelectedQuoteItemIds([]);
    setPendingFile(null);
  };

  const handleQuotationRemove = () => {
    if (selectedQuotationNames.length === 0) return;
    setQuoteData((prev) => {
      const next = [...prev];
      selectedQuotationNames.forEach((name) => {
        const idx = next.findIndex((q) => q.file.name === name);
        if (idx !== -1) {
          setQuoteItemSelection((sel) => ({ ...sel, ...next[idx].items }));
          next.splice(idx, 1);
        }
      });
      return next;
    });
    setSelectedQuotationNames([]);
  };

  // .................... Supplier / shipping / billing ...........................
  const [supplierForm, setSupplierForm] = useState({ ...BLANK_PARTY, quotation: "" });
  const [shippingForm, setShippingForm] = useState(BLANK_PARTY);
  const [billingForm, setBillingForm] = useState(BLANK_PARTY);
  const [billingSameAsShipping, setBillingSameAsShipping] = useState(true);

  const handleShippingChange = (field, value) => {
    setShippingForm((prev) => ({ ...prev, [field]: value }));
    if (billingSameAsShipping) setBillingForm((prev) => ({ ...prev, [field]: value }));
  };
  const handleBillingSameToggle = (checked) => {
    setBillingSameAsShipping(checked);
    setBillingForm(checked ? shippingForm : BLANK_PARTY);
  };

  const formFilled = useMemo(() => {
  const allFilled = (obj) => Object.values(obj).every((v) => String(v).trim() !== "");
  const validContact = (obj) => isValidPhone(obj.contactNumber) && EMAIL_REGEX.test(obj.email);

  return (
    allFilled(supplierForm) && validContact(supplierForm) &&
    allFilled(shippingForm) && validContact(shippingForm) &&
    allFilled(billingForm) && validContact(billingForm)
  );
}, [supplierForm, shippingForm, billingForm]);

  // .................... Terms & conditions ...........................
  const [termsConditions, setTermsConditions] = useState(
    DEFAULT_TERMS.map((text) => ({ checked: true, text })),
  );
  const [customTerms, setCustomTerms] = useState([]);
  const [paymentTerm, setPaymentTerm] = useState("");

  // .................... Preview / submit ...........................
  const [logoUrl, setLogoUrl] = useState(null);
  const [selectedProjectId, setSelectedProjectId] = useState("");
  const [customProject, setCustomProject] = useState({ name: "", id: "" });
  const [notes, setNotes] = useState("");
  const [previewUrl, setPreviewUrl] = useState(null);
  const [previewOpen, setPreviewOpen] = useState(false);

  const poNumView = (lastPoNum || 0) + 1;

  const grandTotal = subtotals.reduce((a, b) => a + (b || 0), 0);

  const getProject = () => {
    if (!selectedProjectId) return "";
    if (selectedProjectId === "custom") return `${customProject.name} (${customProject.id})`;
    const p = PROJECTS.find((p) => p.id === selectedProjectId);
    return p ? `${p.name} (${p.id})` : "";
  };

  // .................... Init on open ...........................
  useEffect(() => {
    if (!open) return;

    setCurrentPage(1);
    setItemChecked(Array(items.length).fill(true));
    setDiscountRate(Array(items.length).fill(0));
    setTaxRate(Array(items.length).fill(0));
    setSubtotals(items.map((i) => i.numUnits * i.unitPrice));
    setQuoteItemSelection(Object.fromEntries(items.map((i) => [i.id, makeItemKey(i)])));
    setSelectedQuoteItemIds([]);
    setPendingFile(null);
    setQuoteData([]);
    setSelectedQuotationNames([]);
    setShippingForm(BLANK_PARTY);
    setBillingForm(BLANK_PARTY);
    setBillingSameAsShipping(true);
    setTermsConditions(DEFAULT_TERMS.map((text) => ({ checked: true, text })));
    setCustomTerms([]);
    setPaymentTerm("");
    setSelectedProjectId("");
    setCustomProject({ name: "", id: "" });
    setNotes("");
    setPreviewUrl(null);

    api
      .get(`${SUPPLIER_BY_NAME}?query=${encodeURIComponent(supplier)}`)
      .then((res) => setSupplierForm({ ...(res.data.supplier || BLANK_PARTY), quotation: "" }))
      .catch(() => setSupplierForm({ ...BLANK_PARTY, quotation: "" }));

    api
      .get(ORG_GET)
      .then((res) => {
        const org = res.data.organization || {};
        setShippingForm((prev) => ({ ...prev, name: org.name || "", address: org.address || "", taxId: org.taxId || "" }));
        setBillingForm((prev) => ({ ...prev, name: org.name || "", address: org.address || "", taxId: org.taxId || "" }));
      })
      .catch(() => {});

    api
      .get(`${FILE_DOWNLOAD_URL}?fileKey=org-images/${user?.organizationId}/purchase-order-logo`)
      .then((res) => setLogoUrl(res.data.downloadUrl))
      .catch(() => setLogoUrl(null));
  }, [open]);

  // .................... PDF generation ...........................

  const collectPOData = () => {
    const poItems = items
      .map((item, index) =>
        itemChecked[index]
          ? {
              name: item.item,
              manufacturer: item.manufacturer,
              catalog: item.catalog,
              numUnits: item.numUnits,
              unitPrice: item.unitPrice,
              discount: discountRate[index],
              tax: taxRate[index],
              subtotal: subtotals[index],
            }
          : null,
      )
      .filter(Boolean);

    const supplierData = {
      name: supplierForm.name,
      address: supplierForm.address,
      person: supplierForm.person,
      phone: formatPhone(supplierForm.contactNumber),
      email: supplierForm.email,
      taxId: supplierForm.taxId,
      quotation: supplierForm.quotation,
    };
    const shippingData = {
      name: shippingForm.name,
      address: shippingForm.address,
      person: shippingForm.person,
      phone: formatPhone(shippingForm.contactNumber),
      email: shippingForm.email,
      taxId: shippingForm.taxId,
    };
    const billingData = billingSameAsShipping
      ? { ...shippingData }
      : {
          name: billingForm.name,
          address: billingForm.address,
          person: billingForm.person,
          phone: formatPhone(billingForm.contactNumber),
          email: billingForm.email,
          taxId: billingForm.taxId,
        };

    const terms = [
      ...termsConditions.filter((t) => t.checked).map((t) => t.text),
      ...customTerms.filter((t) => t.checked && t.text.trim() !== "").map((t) => t.text),
    ];
    const paymentTC = PAYMENT_TERMS.find((t) => t.value === paymentTerm)?.label || "";

    return {
      items: poItems,
      supplier: supplierData,
      shipping: shippingData,
      billing: billingData,
      terms,
      paymentTC,
      user,
      project: getProject(),
      notes,
      grandTotal,
    };
  };

  const generatePdf = async (poNum) => {
  const poData = collectPOData();

  return await generatePurchaseOrderPdf({ poNum, poData, logoUrl });
};
 
  const handlePreview = async () => {
    const doc = await generatePdf(poNumView);
    setPreviewUrl(URL.createObjectURL(doc.output("blob")));
    setPreviewOpen(true);
  };

  const saveDocToS3 = async (doc, destination) => {
    try {
      const res = await api.get(
        `${FILE_UPLOAD_URL}?fileName=${encodeURIComponent("inventory-docs/" + user?.organizationId + destination)}&fileType=${doc.type}`,
      );
      const uploadResponse = await fetch(res.data.uploadUrl, {
        method: "PUT",
        headers: { "Content-Type": doc.type },
        body: doc,
      });
      if (!uploadResponse.ok) throw new Error(`Upload failed with status ${uploadResponse.status}`);
    } catch (error) {
      notify.error("Failed to upload purchase order", error?.message);
    }
  };

  const sendPOEmail = async (poDocBase64, poNum) => {
    try {
      await api.post(EMAIL_PO, {
        subject: `Purchase Order #${poNum}`,
        attachment: { content: poDocBase64, filename: `Purchase Order ${poNum}.pdf` },
      });
    } catch (error) {
      notify.error("Failed to send PO email", error?.message);
    }
  };

  const handleSubmission = async () => {
    setSubmitting(true);
    try {
      const incrementRes = await dispatch(incrementPoNum());
      if (!incrementRes.success) {
        notify.error("PO # was not incremented", incrementRes.error);
        return;
      }
      const poNumNew = incrementRes.poNum;

      const poDoc = await generatePdf(poNumNew);
      poDoc.save(`PO_${poNumNew}.pdf`);
      const poPdfBlob = poDoc.output("blob");
      const poPdfBase64 = poDoc.output("datauristring").split(",")[1];
      const poFileName = `PO${poNumNew}_${Date.now()}`;

      const project = getProject();
      const poItems = items.filter((_, i) => itemChecked[i]);
      const genRes = await dispatch(
        generatePO(poItems.map((i) => i.id), poNumNew, project, poFileName),
      );
      if (!genRes.success) {
        await dispatch(updateLastPoNum(poNumNew - 1));
        notify.error("Failed to update PO # in requests", genRes.error);
        return;
      }

      await saveDocToS3(poPdfBlob, "/purchase-orders/" + poFileName);
      await sendPOEmail(poPdfBase64, poNumNew);

      for (const quote of quoteData) {
        const quoteFileName = `Quotation_${quote.file.name.split(".").slice(0, -1).join("")}_${Date.now()}.${quote.file.name.split(".").pop()}`;
        await saveDocToS3(quote.file, "/quotations/" + quoteFileName);
        await dispatch(applyQuotation(Object.keys(quote.items), quoteFileName));
      }

      notify.success("Purchase Order Created", "PO has been successfully created.");
      setOpen(false);
      onGenerated?.();
    } catch (error) {
      notify.error("Failed to generate purchase order", error?.message);
    } finally {
      setSubmitting(false);
    }
  };

  // .................... Step gating ...........................

  const canGoNext =
    (currentPage === 1 && itemChecked.some(Boolean)) ||
    (currentPage === 2 && true) ||
    (currentPage === 3 && formFilled) ||
    (currentPage === 4 && !!paymentTerm);

  const rowInput =
    "w-full rounded-md border border-slate-200 px-2 py-1.5 text-[12px] outline-none focus:border-brand-500 focus:ring-2 focus:ring-brand-500/20";

    const footer = (
            <PurchaseOrderFooter
              currentPage={currentPage}
              canGoNext={canGoNext}
              submitting={submitting}
              onPrevious={() => setCurrentPage((p) => p - 1)}
              onNext={() => setCurrentPage((p) => p + 1)}
              onPreview={handlePreview}
              onSubmit={handleSubmission}
            />
          );

  return (
    <>
      <button
        type="button"
        disabled={items.length === 0}
        onClick={() => setOpen(true)}
        className="flex w-full items-center justify-center gap-1.5 rounded-lg bg-teal-600/10 px-3 py-2 text-[12px] font-medium text-teal-700 transition hover:bg-teal-600/15 disabled:cursor-not-allowed disabled:opacity-50"
      >
        <LuReceiptText size={14} />
        {cellPhoneView ? "PO" : "Create Purchase Order"}
      </button>

      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        title="Create Purchase Order"
        subtitle={supplier}
        footer={footer}
        widthClass="sm:max-w-3xl"
      >
        <StepIndicator current={currentPage} />

        {currentPage === 1 && (
          <ItemReviewStep
            items={items}
            itemChecked={itemChecked}
            discountRate={discountRate}
            taxRate={taxRate}
            subtotals={subtotals}
            handleItemCheck={handleItemCheck}
            handleDiscountChange={handleDiscountChange}
            handleTaxChange={handleTaxChange}
            rowInput={rowInput}
            money={money}
          />
        )}

        {currentPage === 2 && (
          <QuotationStep
            quoteData={quoteData}
            selectedQuotationNames={selectedQuotationNames}
            setSelectedQuotationNames={setSelectedQuotationNames}
            handleQuotationRemove={handleQuotationRemove}
            quoteItemSelection={quoteItemSelection}
            selectedQuoteItemIds={selectedQuoteItemIds}
            toggleQuoteItemSelect={toggleQuoteItemSelect}
            pendingFile={pendingFile}
            setPendingFile={setPendingFile}
            handleQuoteApply={handleQuoteApply}
          />
        )}

        {currentPage === 3 && (
          <PartyDetailsStep
            supplierForm={supplierForm}
            setSupplierForm={setSupplierForm}
            shippingForm={shippingForm}
            handleShippingChange={handleShippingChange}
            billingForm={billingForm}
            setBillingForm={setBillingForm}
            billingSameAsShipping={billingSameAsShipping}
            handleBillingSameToggle={handleBillingSameToggle}
          />
        )}

        {currentPage === 4 && (
          <TermsStep
            termsConditions={termsConditions}
            setTermsConditions={setTermsConditions}
            customTerms={customTerms}
            setCustomTerms={setCustomTerms}
            paymentTerm={paymentTerm}
            setPaymentTerm={setPaymentTerm}
            PAYMENT_TERMS={PAYMENT_TERMS}
          />
        )}

        {currentPage === 5 && (
          <ReviewStep
            poNumView={poNumView}
            money={money}
            grandTotal={grandTotal}
            selectedProjectId={selectedProjectId}
            setSelectedProjectId={setSelectedProjectId}
            PROJECTS={PROJECTS}
            customProject={customProject}
            setCustomProject={setCustomProject}
            notes={notes}
            setNotes={setNotes}
          />
        )}
      </Dialog>

      <Dialog open={previewOpen} onClose={() => setPreviewOpen(false)} title="PO Preview" widthClass="sm:max-w-4xl">
        <div className="h-[70vh] w-full sm:h-[78vh]">
          {previewUrl && <iframe src={previewUrl} title="PO Preview" className="h-full w-full rounded-lg border border-slate-200" />}
        </div>
      </Dialog>
    </>
  );
}